import { z } from 'zod';
import type { Geometry } from 'geojson';
import type { SitrepSectionType } from 'components/DisasterGPT/hooks/useSitrepsV2';
import type { HazardLayerOption } from './useHazardLayerOptions';

/** System section template id of the OPORD "Layer analytics" section. */
export const LAYER_ANALYTICS_SECTION_TEMPLATE_ID =
  'b0d00001-0000-0000-0000-000000000001';

export const isLayerAnalyticsSection = (
  section: Pick<SitrepSectionType, 'section_template_id'>
): boolean =>
  section.section_template_id === LAYER_ANALYTICS_SECTION_TEMPLATE_ID;

const selectedLayerSchema = z.object({
  value: z.string(),
  label: z.string(),
  kind: z.literal('portal'),
  arcgisItemId: z.string().optional(),
  serviceUrl: z.string().optional(),
  authType: z.enum(['apiKey', 'userAuth']).optional(),
});

/*
 * Every layer result is rendered from the same card template, so wildfire,
 * hurricane and generic layers all save the same shape (`section_data` on the
 * section version):
 *
 *   result ─ cards[] ─┬─ figures[]  → grid of labelled values
 *                     ├─ charts[]   → donut charts
 *                     └─ table      → feature table (name + extra columns)
 *
 * Any part may be empty or null; the renderers show a short message instead.
 */

/** [xmin, ymin, xmax, ymax] in WGS84. */
const extentSchema = z.tuple([z.number(), z.number(), z.number(), z.number()]);

/** A labelled figure. Null value renders as "—"; `unit` is appended if set. */
const figureSchema = z.object({
  label: z.string(),
  value: z.number().nullable(),
  unit: z.string().optional(),
});

/** A donut chart. Slices stay in the legend even when their count is 0. */
const chartSchema = z.object({
  title: z.string(),
  slices: z.array(z.object({ label: z.string(), count: z.number() })),
});

/** One table row: the name column plus display text per extra column key. */
const tableRowSchema = z.object({
  name: z.string(),
  cells: z.record(z.string(), z.string()),
  longitude: z.number().nullable(),
  latitude: z.number().nullable(),
  /** Set for polygon/line features so the map can zoom to the whole area. */
  extent: extentSchema.nullable(),
});

const tableSchema = z.object({
  title: z.string().nullable(),
  /** Header of the leading name column, e.g. "Name" or "Title". */
  nameLabel: z.string(),
  /** Columns shown after the name column, in order. */
  columns: z.array(z.object({ key: z.string(), label: z.string() })),
  rows: z.array(tableRowSchema),
});

const cardSchema = z.object({
  key: z.string(),
  title: z.string().nullable(),
  figures: z.array(figureSchema),
  charts: z.array(chartSchema),
  table: tableSchema.nullable(),
});

const resultSchema = z.object({
  layerValue: z.string(),
  label: z.string(),
  /** Which template built the cards; null for the generic attribute table. */
  hazard: z.enum(['wildfire', 'hurricane']).nullable(),
  /** Features the layer returned inside the report boundary. */
  featureCount: z.number(),
  cards: z.array(cardSchema),
});

const contentSchema = z.object({
  version: z.literal(2),
  selectedLayers: z.array(selectedLayerSchema),
  ranAt: z.string().nullable(),
  results: z.array(resultSchema),
});

export type LayerAnalyticsSelectedLayer = z.infer<typeof selectedLayerSchema>;
export type LayerAnalyticsExtent = z.infer<typeof extentSchema>;
export type LayerAnalyticsFigure = z.infer<typeof figureSchema>;
export type LayerAnalyticsChart = z.infer<typeof chartSchema>;
export type LayerAnalyticsTableRow = z.infer<typeof tableRowSchema>;
export type LayerAnalyticsTable = z.infer<typeof tableSchema>;
export type LayerAnalyticsCard = z.infer<typeof cardSchema>;
export type LayerAnalyticsResult = z.infer<typeof resultSchema>;
export type LayerAnalyticsHazard = NonNullable<LayerAnalyticsResult['hazard']>;

/** A queried feature: its attributes plus a WGS84 location, when it has one. */
export type LayerAnalyticsFeature = {
  attributes: Record<string, unknown>;
  longitude?: number | null;
  latitude?: number | null;
  /** [xmin, ymin, xmax, ymax] of non-point geometries (WGS84). */
  extent?: LayerAnalyticsExtent | null;
  /** Title of the (sub)layer the feature came from. */
  layerTitle?: string | null;
};
export type LayerAnalyticsContent = z.infer<typeof contentSchema>;

export const EMPTY_LAYER_ANALYTICS_CONTENT: LayerAnalyticsContent = {
  version: 2,
  selectedLayers: [],
  ranAt: null,
  results: [],
};

/**
 * The location fields of a feature (or anything else carrying them), as stored
 * on a table row.
 */
export const toRowLocation = ({
  longitude,
  latitude,
  extent,
}: Pick<LayerAnalyticsFeature, 'longitude' | 'latitude' | 'extent'>): Pick<
  LayerAnalyticsTableRow,
  'longitude' | 'latitude' | 'extent'
> => ({
  longitude: longitude ?? null,
  latitude: latitude ?? null,
  extent: extent ?? null,
});

export const NO_FEATURES_MESSAGE = 'No features returned';
export const NO_DATA_MESSAGE = 'No data';

const NO_FIGURE = '—';

/** Display text for a figure: rounded, thousands-separated, with its unit. */
export const formatFigure = ({
  value,
  unit,
}: Pick<LayerAnalyticsFigure, 'value' | 'unit'>): string => {
  if (value === null) return NO_FIGURE;
  const text = Math.round(value).toLocaleString('en-US');
  return unit ? `${text} ${unit}` : text;
};

/** Strips the `kind:` prefix from a hazard option value. */
export const getLayerIdFromValue = (value: string): string =>
  value.slice(value.indexOf(':') + 1);

export const toSelectedLayer = ({
  value,
  label,
  kind,
  arcgisItemId,
  serviceUrl,
  authType,
}: HazardLayerOption): LayerAnalyticsSelectedLayer => ({
  value,
  label,
  kind,
  ...(arcgisItemId ? { arcgisItemId } : {}),
  ...(serviceUrl ? { serviceUrl } : {}),
  ...(authType ? { authType } : {}),
});

export const formatAttributeValue = (value: unknown): string | undefined => {
  if (value === null || value === undefined) return '';
  if (
    typeof value === 'string' ||
    typeof value === 'number' ||
    typeof value === 'boolean'
  ) {
    return String(value);
  }
  return undefined;
};

export const toFiniteNumber = (value: unknown): number | null => {
  if (value === null || value === undefined || value === '') return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

export const toText = (value: unknown): string =>
  formatAttributeValue(value) ?? '';

export const formatFeatureCount = (count: number): string =>
  `${count} ${count === 1 ? 'feature' : 'features'}`;

/**
 * Restores content from a section version's `section_data`; returns empty
 * content when it is missing or does not match the current schema.
 */
export const parseLayerAnalyticsContent = (
  sectionData: unknown
): LayerAnalyticsContent => {
  if (!sectionData) return EMPTY_LAYER_ANALYTICS_CONTENT;
  const parsed = contentSchema.safeParse(sectionData);
  return parsed.success ? parsed.data : EMPTY_LAYER_ANALYTICS_CONTENT;
};

export const NO_BOUNDARY_MESSAGE =
  'No AOR/incident boundary — showing all features.';

const parseGeoJsonGeometry = (
  value: string | Geometry | null | undefined
): Geometry | null => {
  if (!value) return null;
  try {
    const parsed = typeof value === 'string' ? JSON.parse(value) : value;
    return parsed && typeof parsed.type === 'string' ? parsed : null;
  } catch {
    return null;
  }
};

/**
 * Picks the GeoJSON boundary a report is limited to: the AOR's current geometry
 * for AOR reports, otherwise the incident's location for incident reports.
 * Returns null when neither has usable geometry.
 */
export const resolveReportBoundaryGeoJson = ({
  aorId,
  incidentId,
  aors,
  incidents,
}: {
  aorId?: string | null;
  incidentId?: string | null;
  aors: { id: string; current_location_geom?: string | null }[];
  incidents: { id: string; location?: Geometry | string | null }[];
}): Geometry | null => {
  if (aorId) {
    const aor = aors.find((candidate) => candidate.id === aorId);
    return parseGeoJsonGeometry(aor?.current_location_geom);
  }
  if (incidentId) {
    const incident = incidents.find((candidate) => candidate.id === incidentId);
    return parseGeoJsonGeometry(incident?.location);
  }
  return null;
};
